// apps/web/src/useChatStream.ts
import { useCallback, useRef, useState } from "react"
import { API_BASE, apiHeaders, apiUrl } from "./api-base"
import type { ChatFrame, DashboardAction } from "./chat-types"
import { useChat } from "./store"

export type ChatMessage =
  | { id: string; role: "user"; content: string }
  | {
      id: string
      role: "assistant"
      content: string
      toolCalls: Array<{ tool: string; args: Record<string, unknown>; status: "running" | "done" }>
    }

function isAssistant(m: ChatMessage): m is Extract<ChatMessage, { role: "assistant" }> {
  return m.role === "assistant"
}

/**
 * True when an error code/message means "the backend has no Gemini key
 * configured" — surfaced as a setup callout, not a raw transport error.
 */
export function isNotConfiguredError(code?: string, message?: string): boolean {
  const c = (code ?? "").toLowerCase()
  if (c.includes("not_configured") || c.includes("not-configured")) return true
  const m = (message ?? "").toLowerCase()
  return m.includes("gemini_api_key") || m.includes("faketransport")
}

export function useChatStream(endpoint = apiUrl("/api/ai/chat")) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [streaming, setStreaming] = useState(false)
  const [notConfigured, setNotConfigured] = useState(false)
  const apply = useChat((s) => s.applyAction)
  const abortRef = useRef<AbortController | null>(null)

  const send = useCallback(
    async (text: string) => {
      if (streaming) return
      const userMsg: ChatMessage = { id: crypto.randomUUID(), role: "user", content: text }
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        toolCalls: [],
      }
      setMessages((m) => [...m, userMsg, assistantMsg])
      setStreaming(true)

      const appendToAssistant = (suffix: string) =>
        setMessages((m) =>
          m.map((msg) =>
            msg.id === assistantMsg.id && isAssistant(msg)
              ? { ...msg, content: msg.content + suffix }
              : msg,
          ),
        )

      const ctrl = new AbortController()
      abortRef.current = ctrl
      try {
        const resp = await fetch(endpoint, {
          method: "POST",
          credentials: "include",
          headers: apiHeaders({ "content-type": "application/json" }),
          body: JSON.stringify({ message: text }),
          signal: ctrl.signal,
        })
        if (!resp.ok || !resp.body) {
          // The backend may answer with a structured "AI not configured"
          // body instead of an SSE stream — detect it before failing.
          const bodyText = await resp.text().catch(() => "")
          if (isNotConfiguredError(bodyText, bodyText)) {
            setNotConfigured(true)
            appendToAssistant("The AI assistant is not configured on this backend.")
            return
          }
          throw new Error(`chat request failed (HTTP ${resp.status})`)
        }
        const reader = resp.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          let idx: number
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            const chunk = buffer.slice(0, idx)
            buffer = buffer.slice(idx + 2)
            const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "))
            if (!dataLine) continue
            const frame = JSON.parse(dataLine.slice(6)) as ChatFrame
            handleFrame(frame, assistantMsg.id, setMessages, apply, () => setNotConfigured(true))
          }
        }
      } catch (err) {
        if (ctrl.signal.aborted) {
          appendToAssistant("\n[stopped]")
        } else {
          const raw = (err as Error).message
          const friendly = /failed to fetch|networkerror|load failed/i.test(raw)
            ? `Could not reach the API at ${API_BASE} — is the backend running?`
            : raw
          appendToAssistant(`\n[error: ${friendly}]`)
        }
      } finally {
        setStreaming(false)
        abortRef.current = null
      }
    },
    [endpoint, streaming, apply],
  )

  const cancel = useCallback(() => abortRef.current?.abort(), [])

  return { messages, streaming, send, cancel, notConfigured }
}

function handleFrame(
  frame: ChatFrame,
  assistantId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  apply: (a: DashboardAction) => void,
  onNotConfigured: () => void,
) {
  if (frame.type === "token") {
    setMessages((msgs) =>
      msgs.map((m) =>
        m.id === assistantId && isAssistant(m) ? { ...m, content: m.content + frame.text } : m,
      ),
    )
  } else if (frame.type === "tool_call") {
    setMessages((msgs) =>
      msgs.map((m) =>
        m.id === assistantId && isAssistant(m)
          ? {
              ...m,
              toolCalls: [
                ...m.toolCalls,
                { tool: frame.tool, args: frame.args, status: "running" },
              ],
            }
          : m,
      ),
    )
  } else if (frame.type === "tool_result") {
    setMessages((msgs) =>
      msgs.map((m) => {
        if (m.id !== assistantId || !isAssistant(m)) return m
        const next = [...m.toolCalls]
        for (let i = next.length - 1; i >= 0; i--) {
          const item = next[i]
          if (item && item.status === "running") {
            next[i] = { ...item, status: "done" }
            break
          }
        }
        return { ...m, toolCalls: next }
      }),
    )
  } else if (frame.type === "action") {
    apply({ action_type: frame.action_type, payload: frame.payload })
  } else if (frame.type === "error") {
    if (isNotConfiguredError(frame.code, frame.message)) {
      onNotConfigured()
      setMessages((msgs) =>
        msgs.map((m) =>
          m.id === assistantId && isAssistant(m) && m.content === ""
            ? { ...m, content: "The AI assistant is not configured on this backend." }
            : m,
        ),
      )
      return
    }
    setMessages((msgs) =>
      msgs.map((m) =>
        m.id === assistantId && isAssistant(m)
          ? { ...m, content: m.content + `\n[${frame.code}: ${frame.message}]` }
          : m,
      ),
    )
  }
}
